/**
 * How milestone standing is described to a user.
 *
 * The whole point of this module is the `basis` line. A county case record and
 * our floor-count inference are not the same kind of claim, and the UI must
 * never let them look alike — see Terms §8.3 and the AUP §7. Every label here
 * carries its own provenance so a caller cannot render a status without it.
 */

import type { Building, MilestoneStatus } from "../api/types";
import { countyName } from "./counties";

export interface MilestoneCopy {
  /** Short table badge. */
  badge: string | null;
  badgeTone: "b-red" | "b-amber" | "b-grey" | null;
  /** Drawer heading. */
  headline: string;
  /** Where the claim comes from — always shown alongside the headline. */
  basis: string;
  tone: "overdue" | "eligible" | "clear" | "neutral";
  /** True when this came from the county's case file rather than our estimate. */
  authoritative: boolean;
  /**
   * The chip shown beside the headline. Three values, not two: a status we
   * could not decide is neither the county's determination nor our estimate,
   * and calling it an estimate would dress an absence up as an inference.
   */
  provenance: "County record" | "Our estimate" | "Record incomplete";
}

const COPY: Record<MilestoneStatus, Omit<MilestoneCopy, "basis" | "provenance">> = {
  county_enforcement: {
    badge: "enforcement",
    badgeTone: "b-red",
    headline: "Open recertification case, referred to enforcement",
    tone: "overdue",
    authoritative: true,
  },
  county_in_progress: {
    badge: "recert open",
    badgeTone: "b-amber",
    headline: "Recertification case open",
    tone: "eligible",
    authoritative: true,
  },
  county_cycle_due: {
    badge: "cycle due",
    badgeTone: "b-amber",
    headline: "Last recertified more than ten years ago",
    tone: "eligible",
    authoritative: true,
  },
  county_current: {
    badge: null,
    badgeTone: null,
    headline: "Recertification current",
    tone: "clear",
    authoritative: true,
  },
  estimated_overdue: {
    badge: "est. overdue",
    badgeTone: "b-red",
    headline: "Estimated past due for milestone inspection",
    tone: "overdue",
    authoritative: false,
  },
  estimated_eligible: {
    badge: "est. milestone",
    badgeTone: "b-amber",
    headline: "Estimated inside the milestone window",
    tone: "eligible",
    authoritative: false,
  },
  estimated_unknown: {
    badge: "unknown",
    badgeTone: "b-grey",
    headline: "Milestone obligation unknown",
    tone: "neutral",
    authoritative: false,
  },
  not_eligible: {
    badge: null,
    badgeTone: null,
    headline: "No milestone obligation identified",
    tone: "neutral",
    authoritative: false,
  },
};

export function milestoneCopy(building: Building): MilestoneCopy {
  // An unrecognised status falls to `unknown`, never to `not_eligible`: a
  // status this client has not been taught is exactly the case where it has no
  // grounds to tell anyone a building is in the clear.
  const base = COPY[building.milestone_status] ?? COPY.estimated_unknown;
  return { ...base, basis: basisFor(building), provenance: provenanceFor(building) };
}

function provenanceFor(building: Building): MilestoneCopy["provenance"] {
  if (building.milestone_source === "county") return "County record";
  if (building.milestone_status === "estimated_unknown") return "Record incomplete";
  return "Our estimate";
}

/**
 * Counties where the milestone record is held by a *city*, not by the county.
 *
 * Broward's comes from Fort Lauderdale's BSIP permits and Pinellas's from St.
 * Petersburg's milestone register; neither county holds one of its own. Naming
 * the county on those rows attributes a city's determination to a government
 * that never made it — the same false statement as labelling Palm Beach's
 * register "Miami-Dade", and the reason this line names an authority at all.
 */
const MUNICIPAL_MILESTONE_RECORD = new Set(["12011", "12103"]);

/** Whose determination this is, named as precisely as the row allows. */
function authorityFor(building: Building): string {
  if (MUNICIPAL_MILESTONE_RECORD.has(building.county_fips) && building.city) {
    return `${building.city} record`;
  }
  const county = countyName(building.county_fips);
  return county ? `${county} County record` : "County record";
}

function basisFor(building: Building): string {
  if (building.milestone_source === "county") {
    const parts = [authorityFor(building)];
    if (building.recert_case) parts.push(`case ${building.recert_case}`);
    if (building.recert_year) parts.push(String(building.recert_year));
    if (building.recert_status) parts.push(building.recert_status);
    return parts.join(" · ");
  }

  if (building.milestone_status === "not_eligible") {
    return "No county milestone or recertification case found for this address.";
  }

  // The one status that describes our record rather than the building. Naming
  // which half is missing is the difference between "we checked" and "we could
  // not check" — and § 553.899 turns on exactly those two numbers.
  if (building.milestone_status === "estimated_unknown") {
    const missing = !building.derived_floors && !building.year_built
      ? "neither a storey count nor a year built"
      : !building.derived_floors
        ? "no storey count"
        : "no year built";
    return `The county publishes ${missing} for this building, so whether Fla. Stat. § 553.899 applies to it cannot be told from the record. This is not a finding that it is exempt.`;
  }

  const floors = building.derived_floors
    ? `${building.derived_floors} floors (derived from unit numbering)`
    : "an unknown floor count";
  const age = building.year_built ? `built ${building.year_built}` : "unknown age";
  return `Our estimate from ${floors} and ${age}. No county record matched this address — this is not an official determination.`;
}
